import { Link } from 'react-router-dom';
import { formatMoney } from '../brand/brand';
import type { WalletSummary } from '../api/types';

/**
 * What the customer holds, grouped by partner. SOW §2.2 and §2.3.
 *
 * The wallet used to list vouchers one at a time, so somebody holding three vouchers
 * for the same restaurant saw three rows and did the addition themselves. A partner
 * is the unit people think in — "how much have I got left at the Italian place" — so
 * that is the unit this shows.
 *
 * Nothing renders when there is one partner or none: a "grouped by partner" heading
 * above a single group is furniture, and the voucher list below already says it.
 */
export function PartnerBalances({ summary }: { summary: WalletSummary | null }) {
  if (summary === null || summary.partners.length < 2) {
    return null;
  }

  return (
    <section className="stack" style={{ gap: 10, marginBottom: 22 }}>
      <h2>Your balances</h2>

      <ul className="partner-balances">
        {summary.partners.map((p) => {
          const expiring = soonestDays(p.nextExpiryUtc);

          return (
            <li key={p.businessId} className="partner-balance">
              <span className="partner-balance-main">
                <span className="partner-balance-name">{p.businessName}</span>
                <span className="acct-row-sub">
                  {p.voucherCount === 1 ? '1 voucher' : `${p.voucherCount} vouchers`}
                  {/*
                    Warned about only inside a month. A date thirteen months out is
                    not news, and a badge on every row teaches people to ignore the
                    badge on the row that matters.
                  */}
                  {expiring !== null && expiring <= 30 && (
                    <> · <span className="expiring">
                      {expiring <= 0 ? 'expires today' : `expires in ${expiring} day${expiring === 1 ? '' : 's'}`}
                    </span></>
                  )}
                </span>
              </span>

              <span className="partner-balance-amount">{formatMoney(p.balanceCents)}</span>
            </li>
          );
        })}
      </ul>

      <p className="hint">
        Total <strong>{formatMoney(summary.totalBalanceCents)}</strong> — each partner&rsquo;s
        credit is spendable with them only. <Link to="/">Find more offers</Link>
      </p>
    </section>
  );
}

/** Whole days until expiry, or null when there is no date. */
function soonestDays(iso: string | null): number | null {
  if (iso === null) return null;

  const ms = new Date(iso).getTime() - Date.now();

  // Rounded UP, so "expires in 1 day" never appears on something that lapses in
  // twenty minutes.
  return Math.ceil(ms / 86_400_000);
}
